// useMediaCapture — Live 與回放共用的「截圖 + 錄影到本機」hook（APP#17）。
//
// 截圖：抓 <video> 當前 frame 畫到 canvas → toBlob → <a download> 觸發下載；
//       若 navigator.canShare 可用且支援檔案分享，提供 Web Share。
// 錄影：優先錄傳入的 WebRTC MediaStream，否則 video.captureStream()；mime 由
//       pickRecorderMime() 偵測（mp4 優先）。錄影中以 1s tick 更新 elapsed。
//       停止 → 收集 chunks → blob → 下載。卸載時自動停止並落檔。
//
// 純邏輯（檔名 / mime / 計時格式）在 captureLib.ts，單元測試覆蓋；本 hook 只
// 編排副作用（canvas / MediaRecorder / 下載），其失敗皆降級為狀態旗標不丟例外。

import { useCallback, useEffect, useRef, useState } from 'react';
import { captureFilename, extFromMime, pickRecorderMime } from './captureLib';
import { addItem, newItemId, type AddResult } from '../gallery/galleryDb';
import { kindFromMime } from '../gallery/galleryLib';

export type CaptureFailure = 'no-frame' | 'encode' | 'unsupported' | 'start-failed' | 'quota' | null;

export interface MediaCapture {
  /** 截圖：成功回 true（已觸發下載），無畫面/失敗回 false。 */
  screenshot(): Promise<boolean>;
  /** 是否錄影中。 */
  recording: boolean;
  /** 開始錄影；不支援時設定 lastError='unsupported' 並回 false。 */
  startRecording(): boolean;
  /** 停止錄影並落檔。 */
  stopRecording(): void;
  /** 錄影經過秒數（未錄影為 0）。 */
  elapsed: number;
  /** 最近一次操作的失敗原因（成功時 null）。 */
  lastError: CaptureFailure;
  /** 此瀏覽器是否支援錄影（MediaRecorder + 可用 mime）。 */
  canRecord: boolean;
}

/**
 * 存一份到本地相簿（APP#42）。回傳 addItem 結果供呼叫端偵測配額超限（'quota'）
 * 以提示使用者；其餘失敗（含無 IndexedDB 的環境）靜默略過，不影響截圖/錄影主流程
 * （仍有下載/分享）。
 */
function saveToGallery(
  blob: Blob,
  mime: string,
  filename: string,
  deviceName: string,
  deviceId: string | undefined,
): Promise<AddResult> {
  return addItem({
    id: newItemId(),
    kind: kindFromMime(mime),
    blob,
    mime,
    filename,
    deviceId,
    deviceName,
    createdAt: Date.now(),
    size: blob.size,
  });
}

/** 觸發瀏覽器下載一個 Blob（建立暫時 object URL，事後撤銷）。 */
function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // 給瀏覽器時間排程下載再撤銷 URL
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * @param videoRef     要擷取的 <video> 元素（截圖來源、錄影 fallback 來源）
 * @param mediaStream  WebRTC 串流（優先用於錄影；回放頁傳 null → 走 captureStream）
 * @param deviceName   下載檔名前綴 + 相簿裝置顯示名
 * @param deviceId     選填：來源裝置 id（存入相簿供裝置層過濾，APP#42）
 */
export function useMediaCapture(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  mediaStream: MediaStream | null,
  deviceName: string,
  deviceId?: string,
): MediaCapture {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [lastError, setLastError] = useState<CaptureFailure>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedAtRef = useRef(0);
  // 捕捉 captureStream() 產生的串流以便停止後釋放（WebRTC 串流由 Live 頁自行管理）。
  const ownedStreamRef = useRef<MediaStream | null>(null);
  const nameRef = useRef(deviceName);
  nameRef.current = deviceName;
  const idRef = useRef(deviceId);
  idRef.current = deviceId;
  const streamRef = useRef<MediaStream | null>(mediaStream);
  streamRef.current = mediaStream;

  const canRecord = typeof MediaRecorder !== 'undefined' && pickRecorderMime() !== null;

  const clearTick = () => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  };

  const screenshot = useCallback(async (): Promise<boolean> => {
    const v = videoRef.current;
    if (!v || !v.videoWidth || !v.videoHeight) {
      setLastError('no-frame');
      return false;
    }
    try {
      const canvas = document.createElement('canvas');
      canvas.width = v.videoWidth;
      canvas.height = v.videoHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        setLastError('encode');
        return false;
      }
      ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((b) => resolve(b), 'image/png');
      });
      if (!blob) {
        setLastError('encode');
        return false;
      }
      const filename = captureFilename(nameRef.current, 'png');
      // 本地相簿留存（APP#42）：不論後續走分享或下載，都先存一份供相簿瀏覽。
      // 容量不足（quota）時提示使用者存不下（下載/分享仍照常進行）。
      void saveToGallery(blob, 'image/png', filename, nameRef.current, idRef.current).then((r) => {
        if (r === 'quota') setLastError('quota');
      });
      // Web Share API（行動裝置）：可分享檔案時優先提供分享，否則直接下載。
      const file = typeof File !== 'undefined' ? new File([blob], filename, { type: 'image/png' }) : null;
      const nav = navigator as Navigator & {
        canShare?: (data?: { files?: File[] }) => boolean;
        share?: (data: { files?: File[]; title?: string }) => Promise<void>;
      };
      if (file && typeof nav.canShare === 'function' && nav.canShare({ files: [file] }) && typeof nav.share === 'function') {
        try {
          await nav.share({ files: [file], title: filename });
          setLastError(null);
          return true;
        } catch {
          // 使用者取消分享 → 退回下載
        }
      }
      triggerDownload(blob, filename);
      setLastError(null);
      return true;
    } catch {
      setLastError('encode');
      return false;
    }
  }, [videoRef]);

  const stopRecording = useCallback((): void => {
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') {
      try {
        rec.stop(); // onstop 收尾落檔
      } catch {
        /* already stopped */
      }
    }
    clearTick();
  }, []);

  const startRecording = useCallback((): boolean => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') return true;
    const mime = pickRecorderMime();
    if (typeof MediaRecorder === 'undefined' || !mime) {
      setLastError('unsupported');
      return false;
    }
    // 來源：優先 WebRTC MediaStream，否則 video.captureStream()。
    let stream = streamRef.current;
    ownedStreamRef.current = null;
    if (!stream) {
      const v = videoRef.current as (HTMLVideoElement & { captureStream?: () => MediaStream }) | null;
      if (v?.captureStream) {
        try {
          stream = v.captureStream();
          ownedStreamRef.current = stream;
        } catch {
          stream = null;
        }
      }
    }
    if (!stream) {
      setLastError('start-failed');
      return false;
    }
    try {
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e: BlobEvent) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mime });
        chunksRef.current = [];
        if (blob.size > 0) {
          const filename = captureFilename(nameRef.current, extFromMime(mime));
          // 本地相簿留存（APP#42）+ 下載。容量不足時提示使用者（下載仍照常）。
          void saveToGallery(blob, mime, filename, nameRef.current, idRef.current).then((r) => {
            if (r === 'quota') setLastError('quota');
          });
          triggerDownload(blob, filename);
        }
        // 釋放自有的 captureStream
        if (ownedStreamRef.current) {
          ownedStreamRef.current.getTracks().forEach((t) => t.stop());
          ownedStreamRef.current = null;
        }
        recorderRef.current = null;
        setRecording(false);
        setElapsed(0);
      };
      rec.start(1000); // 每秒一個 chunk → 中途斷線也保留已錄部分
      recorderRef.current = rec;
      startedAtRef.current = Date.now();
      setElapsed(0);
      setRecording(true);
      setLastError(null);
      clearTick();
      tickRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
      }, 500);
      return true;
    } catch {
      setLastError('start-failed');
      return false;
    }
  }, [videoRef]);

  // 卸載：自動停止並落檔（離開頁面/斷線）。
  useEffect(() => {
    return () => {
      stopRecording();
    };
  }, [stopRecording]);

  // WebRTC 串流消失（斷線）時，若正在錄影則自動收尾落檔。
  useEffect(() => {
    if (recording && mediaStream === null && streamRef.current === null && recorderRef.current) {
      // 只有當錄的是 WebRTC 串流（非 captureStream）時才因串流消失而停
      if (!ownedStreamRef.current) stopRecording();
    }
  }, [mediaStream, recording, stopRecording]);

  return { screenshot, recording, startRecording, stopRecording, elapsed, lastError, canRecord };
}
